import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { IntegrationsSettings } from "@/components/settings/IntegrationsSettings";
import { ServicesManagement } from "@/components/settings/ServicesManagement";
import { UsersManagement } from "@/components/settings/UsersManagement";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Settings as SettingsIcon, Activity } from "lucide-react";
import { useAuthStore } from "@/lib/auth";
import { ProfileSettings } from "@/components/settings/ProfileSettings";
import { SubscriptionStatus } from "@/components/ui/subscription-status";
import { PerformanceStatus } from "@/components/ui/performance-status";
import { NameChangeAuditLog } from "@/components/patients/NameChangeAuditLog";

const tabTriggerClass =
  "text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50";

const Settings = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("profile");
  const [showAuditLog, setShowAuditLog] = useState(false);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      setActiveTab(tab === "general" ? "integrations" : tab);
    }
  }, [searchParams]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <SettingsIcon className="h-6 w-6 text-hospital-primary" />
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid grid-cols-2 sm:grid-cols-3 mb-6 w-full lg:grid-cols-6">
            <TabsTrigger value="profile" className={tabTriggerClass}>
              Profile
            </TabsTrigger>
            <TabsTrigger value="users" className={tabTriggerClass}>
              Users
            </TabsTrigger>
            <TabsTrigger value="services" className={tabTriggerClass}>
              Services
            </TabsTrigger>
            <TabsTrigger value="notifications" className={tabTriggerClass}>
              <span className="hidden sm:inline">Notifications</span>
              <span className="sm:hidden">Notif</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className={tabTriggerClass}>
              <span className="hidden sm:inline">Integrations</span>
              <span className="sm:hidden">Integrate</span>
            </TabsTrigger>
            <TabsTrigger value="system" className={tabTriggerClass}>
              System
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileSettings />
          </TabsContent>

          <TabsContent value="users">
            <UsersManagement />
          </TabsContent>

          <TabsContent value="services">
            <ServicesManagement />
          </TabsContent>

          <TabsContent value="notifications">
            <div className="space-y-6">
              {isAdmin && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Patient Name Changes Audit</h3>
                      <p className="text-sm text-muted-foreground">
                        Track all patient name changes for compliance and audit purposes
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowAuditLog(true)}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Activity className="h-4 w-4" />
                      View Audit Log
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="integrations">
            <IntegrationsSettings />
          </TabsContent>

          <TabsContent value="system">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-hospital-primary" />
                <h2 className="text-lg font-semibold">System Status</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SubscriptionStatus />
                <PerformanceStatus />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <NameChangeAuditLog isOpen={showAuditLog} onClose={() => setShowAuditLog(false)} />
    </MainLayout>
  );
};

export default Settings;
